// Parser for custom DSL syntax to generate database schema
// Supports syntax like:
// table_name {
//   id bigint pk increments
//   name varchar(100)
//   foreign_id bigint > other_table.id
// }

import { v4 as uuidv4 } from 'uuid';

export const parseDSL = (dslText) => {
  const tables = [];
  const relationships = [];
  
  // Remove comments and extra whitespace
  const cleanText = dslText
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && !line.startsWith('//'))
    .join('\n');
  
  // Match table blocks
  const tableRegex = /(\w+)\s*{([^}]+)}/g;
  let match;
  
  const tableMap = {}; // Store table IDs by name
  let xPos = 100;
  let yPos = 100;
  
  while ((match = tableRegex.exec(cleanText)) !== null) {
    const tableName = match[1];
    const tableBody = match[2];
    
    const tableId = uuidv4();
    tableMap[tableName] = tableId;
    
    const columns = [];
    const columnMap = {}; // Store column IDs by name
    
    // Parse columns
    const lines = tableBody.split('\n').map(l => l.trim()).filter(l => l);
    
    lines.forEach(line => {
      // Parse column definition
      // Format: column_name type [options] [> reference]
      const parts = line.split(/\s+/);
      if (parts.length < 2) return;
      
      const columnName = parts[0];
      const columnId = uuidv4();
      columnMap[columnName] = columnId;
      
      // Parse data type
      let dataType = parts[1].toUpperCase();
      let length = null;
      
      // Extract length from type like VARCHAR(100) or BIGINT(11)
      const lengthMatch = dataType.match(/(\w+)\((\d+)(?:,\d+)?\)/);
      if (lengthMatch) {
        dataType = lengthMatch[1];
        length = parseInt(lengthMatch[2]);
      }
      
      // Parse options
      let isPrimaryKey = false;
      let isAutoIncrement = false;
      let isNullable = true;
      let defaultValue = '';
      let hasRelation = false;
      let relationTarget = '';
      
      for (let i = 2; i < parts.length; i++) {
        const opt = parts[i].toLowerCase();
        
        if (opt === 'pk') isPrimaryKey = true;
        if (opt === 'increments') isAutoIncrement = true;
        if (opt === 'unique') isNullable = false;
        if (opt === 'not' && parts[i + 1]?.toLowerCase() === 'null') {
          isNullable = false;
          i++;
        }
        if (opt.startsWith('def(')) {
          defaultValue = opt.match(/def\((.+)\)/)?.[1] || '';
        }
        if (opt === '>') {
          hasRelation = true;
          relationTarget = parts[i + 1];
        }
      }
      
      columns.push({
        id: columnId,
        name: columnName,
        data_type: dataType,
        length: length,
        is_primary_key: isPrimaryKey,
        is_auto_increment: isAutoIncrement,
        is_nullable: isNullable,
        default_value: defaultValue
      });
      
      // Store relationship for later processing
      if (hasRelation && relationTarget) {
        const [targetTable, targetColumn] = relationTarget.split('.');
        relationships.push({
          sourceTable: tableName,
          sourceColumn: columnName,
          targetTable: targetTable,
          targetColumn: targetColumn
        });
      }
    });
    
    tables.push({
      id: tableId,
      name: tableName,
      columns: columns,
      position: { x: xPos, y: yPos },
      columnMap: columnMap
    });
    
    // Position tables in a grid
    xPos += 350;
    if (xPos > 1200) {
      xPos = 100;
      yPos += 400;
    }
  }
  
  // Process relationships
  const processedRelationships = [];
  relationships.forEach(rel => {
    const sourceTable = tables.find(t => t.name === rel.sourceTable);
    const targetTable = tables.find(t => t.name === rel.targetTable);
    
    if (sourceTable && targetTable) {
      const sourceColumnId = sourceTable.columnMap[rel.sourceColumn];
      const targetColumnId = targetTable.columnMap[rel.targetColumn];
      
      if (sourceColumnId && targetColumnId) {
        processedRelationships.push({
          id: uuidv4(),
          source_table_id: sourceTable.id,
          source_column_id: sourceColumnId,
          target_table_id: targetTable.id,
          target_column_id: targetColumnId,
          relationship_type: 'many-to-one',
          on_delete: 'CASCADE',
          on_update: 'CASCADE'
        });
      }
    }
  });
  
  return {
    tables: tables.map(t => ({
      id: t.id,
      name: t.name,
      columns: t.columns,
      position: t.position
    })),
    relationships: processedRelationships
  };
};
